import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "d-flex justify-center align-center py-4"
}
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { class: "mr-1" }
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = { class: "d-flex align-center" }
const _hoisted_6 = {
  key: 1,
  class: "font-weight-medium",
  style: {"margin-left":"4px","width":"100%","text-align":"center","display":"block"}
}
const _hoisted_7 = { class: "d-flex justify-center" }

import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './components/AdminLayout.vue'
import { useAuthStore } from './stores/authStore'
import { useWorkspaceStore } from './stores/workspaceStore'

interface User {
  id: string
  name: string
  receivedPoints: number
  givenPoints: number
  redeemablePoints: number
  role: string
  active: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'People',
  setup(__props) {

const router = useRouter()
const { checkAuth } = useAuthStore()
const { getWorkspaceEmoji } = useWorkspaceStore()
const workspaceEmoji = getWorkspaceEmoji()

const users = ref<User[]>([])
const loading = ref(true)
const error = ref('')
const currentUser = ref<{ id: string; isAdmin: boolean } | null>(null)
const showDeactivateDialog = ref(false)
const userToDeactivate = ref<User | null>(null)

// Computed property to check if a user can change a role
const canChangeRole = (userId: string) => {
  if (!currentUser.value) return false
  // Only admins can change roles, and they cannot change their own role
  return currentUser.value.isAdmin && currentUser.value.id !== userId
}

const headers = [
  { title: 'Handle', key: 'name', align: 'start' as const },
  { title: 'Received', key: 'receivedPoints', align: 'center' as const },
  { title: 'Given', key: 'givenPoints', align: 'center' as const },
  { title: 'Redeemable Tokens', key: 'redeemablePoints', align: 'center' as const },
  { title: 'Role', key: 'role', align: 'center' as const },
  { title: 'Actions', key: 'actions', align: 'center' as const }
]

async function updateUserRole(userId: string, newRole: string) {
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${userId}/role`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to update user role')
    }
  } catch (e) {
    console.error('Error updating user role:', e)
    error.value = e instanceof Error ? e.message : 'Failed to update user role. Please try again.'
    // Refresh the users list to revert any UI changes
    await fetchUsers()
  }
}

function confirmDeactivateUser(user: User) {
  userToDeactivate.value = user
  showDeactivateDialog.value = true
}

async function deactivateUser() {
  if (!userToDeactivate.value) return
  
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${userToDeactivate.value.id}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to deactivate user')
    }

    // Update the user's active status in the local list
    const userIndex = users.value.findIndex(user => user.id === userToDeactivate.value!.id)
    if (userIndex !== -1) {
      users.value[userIndex].active = false
    }
    
    // Close dialog and reset
    showDeactivateDialog.value = false
    userToDeactivate.value = null
    
    // Show success message
    error.value = ''
  } catch (e) {
    console.error('Error deactivating user:', e)
    error.value = e instanceof Error ? e.message : 'Failed to deactivate user. Please try again.'
  }
}

async function reactivateUser(user: User) {
  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users/${user.id}/reactivate`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to reactivate user')
    }

    // Update the user's active status in the local list
    const userIndex = users.value.findIndex(u => u.id === user.id)
    if (userIndex !== -1) {
      users.value[userIndex].active = true
    }
    
    // Show success message
    error.value = ''
  } catch (e) {
    console.error('Error reactivating user:', e)
    error.value = e instanceof Error ? e.message : 'Failed to reactivate user. Please try again.'
  }
}

async function fetchUsers() {
  loading.value = true
  error.value = ''
  
  try {
    // Check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    // Set current user info
    currentUser.value = {
      id: authData.userHandle || '',
      isAdmin: authData.isAdmin
    }

    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/admin/users`,
      { credentials: 'include' }
    )
    
    if (!response.ok) {
      throw new Error('Failed to fetch users')
    }
    
    const data = await response.json()
    users.value = data.map((user: any) => ({
      id: user.id || '',
      name: user.name || '',
      receivedPoints: user.receivedPoints || 0,
      givenPoints: user.givenPoints || 0,
      redeemablePoints: user.redeemablePoints || 0,
      role: user.role || 'Member',
      active: user.active !== false // Default to true if not specified
    }))
  } catch (e) {
    error.value = 'Failed to load users. Please try again later.'
    console.error('Error fetching users:', e)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchUsers()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { elevation: "2" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex align-center" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_icon, {
                icon: "mdi-account-group",
                class: "mr-2"
              }),
              _cache[2] || (_cache[2] = _createTextVNode(" People ", -1))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              (loading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(_component_v_progress_circular, {
                      indeterminate: "",
                      color: "primary"
                    })
                  ]))
                : (error.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 1,
                      type: "error",
                      class: "my-4"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(error.value), 1)
                      ]),
                      _: 1
                    }))
                  : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createVNode(_component_v_data_table, {
                        headers: headers,
                        items: users.value,
                        "items-per-page": 10,
                        class: "elevation-0"
                      }, {
                        "item.receivedPoints": _withCtx(({ item }) => [
                          _createVNode(_component_v_chip, {
                            color: "success",
                            variant: "flat",
                            size: "small",
                            class: "font-weight-medium"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(item.receivedPoints), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        "item.givenPoints": _withCtx(({ item }) => [
                          _createVNode(_component_v_chip, {
                            color: "info",
                            variant: "flat",
                            size: "small",
                            class: "font-weight-medium"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(item.givenPoints), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        "item.redeemablePoints": _withCtx(({ item }) => [
                          _createVNode(_component_v_chip, {
                            color: "primary",
                            variant: "flat",
                            size: "small",
                            class: "font-weight-medium"
                          }, {
                            prepend: _withCtx(() => [
                              _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(workspaceEmoji).emojiSymbol), 1)
                            ]),
                            default: _withCtx(() => [
                              _createTextVNode(" " + _toDisplayString(item.redeemablePoints), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        "item.name": _withCtx(({ item }) => [
                          _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("span", {
                              style: _normalizeStyle(currentUser.value && currentUser.value.id === item.name ? 'color: #1976d2; font-weight: 600;' : '')
                            }, " @" + _toDisplayString(item.name), 5),
                            (!item.active)
                              ? (_openBlock(), _createBlock(_component_v_chip, {
                                  key: 0,
                                  color: "warning",
                                  variant: "flat",
                                  size: "x-small",
                                  class: "ml-2"
                                }, {
                                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                    _createTextVNode(" Inactive ", -1)
                                  ]))]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        "item.role": _withCtx(({ item }) => [
                          _createElementVNode("div", _hoisted_5, [
                            (currentUser.value && currentUser.value.id !== item.id && currentUser.value.id !== item.name)
                              ? (_openBlock(), _createBlock(_component_v_select, {
                                  key: 0,
                                  modelValue: item.role,
                                  "onUpdate:modelValue": [($event: any) => ((item.role) = $event), ($event: any) => (updateUserRole(item.id, $event))],
                                  items: ['Admin', 'Member'],
                                  variant: "outlined",
                                  density: "compact",
                                  "hide-details": "",
                                  disabled: !canChangeRole(item.id)
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"]))
                              : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(item.role), 1))
                          ])
                        ]),
                        "item.actions": _withCtx(({ item }) => [
                          _createElementVNode("div", _hoisted_7, [
                            (currentUser.value && currentUser.value.id !== item.id && currentUser.value.id !== item.name && item.active)
                              ? (_openBlock(), _createBlock(_component_v_btn, {
                                  key: 0,
                                  variant: "text",
                                  color: "grey-darken-1",
                                  size: "small",
                                  disabled: !canChangeRole(item.id),
                                  onClick: ($event: any) => (confirmDeactivateUser(item))
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, { icon: "mdi-delete" }),
                                    _createVNode(_component_v_tooltip, {
                                      activator: "parent",
                                      location: "top"
                                    }, {
                                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                        _createTextVNode(" Deactivate User (will auto-reactivate if they participate) ", -1)
                                      ]))]),
                                      _: 1
                                    })
                                  ]),
                                  _: 2
                                }, 1032, ["disabled", "onClick"]))
                              : _createCommentVNode("", true),
                            (currentUser.value && currentUser.value.id !== item.id && currentUser.value.id !== item.name && !item.active)
                              ? (_openBlock(), _createBlock(_component_v_btn, {
                                  key: 1,
                                  variant: "text",
                                  color: "success",
                                  size: "small",
                                  disabled: !canChangeRole(item.id),
                                  onClick: ($event: any) => (reactivateUser(item))
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_icon, { icon: "mdi-refresh" }),
                                    _createVNode(_component_v_tooltip, {
                                      activator: "parent",
                                      location: "top"
                                    }, {
                                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                        _createTextVNode(" Reactivate User ", -1)
                                      ]))]),
                                      _: 1
                                    })
                                  ]),
                                  _: 2
                                }, 1032, ["disabled", "onClick"]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 1
                      }, 8, ["items"])
                    ]))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showDeactivateDialog.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showDeactivateDialog).value = $event)),
        "max-width": "400"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    icon: "mdi-alert",
                    color: "warning",
                    class: "mr-2"
                  }),
                  _cache[6] || (_cache[6] = _createTextVNode(" Confirm User Deactivation ", -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _cache[7] || (_cache[7] = _createTextVNode(" Are you sure you want to deactivate ", -1)),
                  _createElementVNode("strong", null, "@" + _toDisplayString(userToDeactivate.value?.name), 1),
                  _cache[8] || (_cache[8] = _createTextVNode("? ", -1)),
                  _cache[9] || (_cache[9] = _createElementVNode("br", null, null, -1)),
                  _cache[10] || (_cache[10] = _createElementVNode("br", null, null, -1)),
                  _cache[11] || (_cache[11] = _createTextVNode(" This action will: ", -1)),
                  _cache[12] || (_cache[12] = _createElementVNode("ul", { class: "mt-2" }, [
                    _createElementVNode("li", null, "Deactivate the user from Prec8"),
                    _createElementVNode("li", null, "User's points are kept in case user is reactivated"),
                    _createElementVNode("li", null, "Users will be automatically reactivated if they give or receive points")
                  ], -1))
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey-darken-1",
                    variant: "text",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (showDeactivateDialog.value = false))
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode(" Cancel ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "error",
                    variant: "elevated",
                    onClick: deactivateUser,
                    loading: loading.value
                  }, {
                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                      _createTextVNode(" Deactivate User ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})